import { metaLines } from "./export.js";
import { zip } from "./zip.js";
import { paperOf } from "./paper.js";
// The zip is zip.ts, shared with the Word document; re-exported for those that read it from here.
export { crc32, zip } from "./zip.js";
// ── the sheet ───────────────────────────────────────────────────────────────────────────────
/** Text safe inside XML: escaped, and without the control characters XML 1.0 cannot carry at all. */
export function xmlText(v) {
    // eslint-disable-next-line no-control-regex
    return v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
/** A1 addressing: 0 → A, 25 → Z, 26 → AA. */
export function columnName(index) {
    let n = index + 1;
    let s = '';
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}
const EPOCH = Date.UTC(1899, 11, 30);
/** A spreadsheet's day number for wall-clock parts: the parts are the day, whatever the timezone. */
function serial(y, mo, d, h = 0, mi = 0, s = 0) {
    return (Date.UTC(y, mo - 1, d, h, mi, s) - EPOCH) / 86_400_000;
}
/** The number a typed cell writes, or null when the value is not one — then it is written as its text. */
export function cellNumber(kind, raw) {
    if (raw === null || raw === undefined || raw === '')
        return null;
    switch (kind) {
        case 'number':
        case 'money': {
            const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
            return Number.isFinite(n) ? n : null;
        }
        case 'count': {
            const n = Array.isArray(raw) ? raw.length : Number(raw);
            return Number.isFinite(n) ? n : null;
        }
        case 'date': {
            // A calendar day is a day: read from its digits, never through an instant and a timezone.
            const m = typeof raw === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(raw) : null;
            return m ? serial(Number(m[1]), Number(m[2]), Number(m[3])) : null;
        }
        case 'datetime': {
            // A moment is shown on screen in the reader's time; the workbook says the same wall clock.
            const d = new Date(String(raw));
            if (Number.isNaN(d.getTime()))
                return null;
            return serial(d.getFullYear(), d.getMonth() + 1, d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds());
        }
        case 'time': {
            const m = typeof raw === 'string' ? /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw) : null;
            if (!m || Number(m[1]) > 23 || Number(m[2]) > 59)
                return null;
            return (Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] ?? 0)) / 86_400;
        }
        default: return null;
    }
}
// The cell formats, by position in <cellXfs> below. Named so the sheet never writes a bare index.
const S = { plain: 0, title: 1, meta: 2, head: 3, money: 4, date: 5, datetime: 6, time: 7, count: 8, number: 9, total: 16 };
const TONE_STYLE = { green: 10, amber: 11, red: 12, blue: 13, violet: 14, neutral: 15 };
const KIND_STYLE = { text: S.plain, number: S.number, money: S.money, count: S.count, date: S.date, datetime: S.datetime, time: S.time };
// The six tones as a tenth of their ink over white — the same pills the screen draws, in a cell.
const TONE_FILL = { green: 'FFDCF5E6', amber: 'FFFDF0D5', red: 'FFFBE0E0', blue: 'FFDDEBFB', violet: 'FFEDE6FB', neutral: 'FFEEF0F3' };
const TONE_INK = { green: 'FF166534', amber: 'FF92400E', red: 'FF991B1B', blue: 'FF1E40AF', violet: 'FF5B21B6', neutral: 'FF374151' };
const TONE_ORDER = ['green', 'amber', 'red', 'blue', 'violet', 'neutral'];
function styles() {
    const fonts = [
        '<font><sz val="11"/><name val="Arial"/><family val="2"/></font>',
        '<font><b/><sz val="11"/><name val="Arial"/><family val="2"/></font>',
        '<font><b/><sz val="14"/><name val="Arial"/><family val="2"/></font>',
        '<font><sz val="10"/><color rgb="FF5F6368"/><name val="Arial"/><family val="2"/></font>',
        ...TONE_ORDER.map((t) => `<font><b/><sz val="11"/><color rgb="${TONE_INK[t]}"/><name val="Arial"/><family val="2"/></font>`),
    ];
    const fills = [
        '<fill><patternFill patternType="none"/></fill>',
        '<fill><patternFill patternType="gray125"/></fill>',
        '<fill><patternFill patternType="solid"><fgColor rgb="FFF1F3F5"/><bgColor indexed="64"/></patternFill></fill>',
        ...TONE_ORDER.map((t) => `<fill><patternFill patternType="solid"><fgColor rgb="${TONE_FILL[t]}"/><bgColor indexed="64"/></patternFill></fill>`),
    ];
    const borders = [
        '<border><left/><right/><top/><bottom/><diagonal/></border>',
        '<border><left/><right/><top/><bottom style="thin"><color rgb="FF9AA0A6"/></bottom><diagonal/></border>',
    ];
    const xf = (numFmt, font, fill, border, extra = '') => `<xf numFmtId="${numFmt}" fontId="${font}" fillId="${fill}" borderId="${border}" xfId="0"${numFmt ? ' applyNumberFormat="1"' : ''}${font ? ' applyFont="1"' : ''}${fill ? ' applyFill="1"' : ''}${border ? ' applyBorder="1"' : ''}${extra ? ` applyAlignment="1">${extra}</xf>` : '/>'}`;
    const xfs = [
        xf(0, 0, 0, 0), // plain
        xf(0, 2, 0, 0), // title
        xf(0, 3, 0, 0), // meta
        xf(0, 1, 2, 1, '<alignment vertical="center" wrapText="1"/>'), // head
        xf(164, 0, 0, 0), // money
        xf(165, 0, 0, 0), // date
        xf(166, 0, 0, 0), // datetime
        xf(167, 0, 0, 0), // time
        xf(3, 0, 0, 0), // count
        xf(0, 0, 0, 0), // number
        ...TONE_ORDER.map((_, i) => xf(0, 4 + i, 3 + i, 0)), // the six tones
        xf(0, 1, 0, 0), // total: the words, bold
    ];
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
        + '<numFmts count="4"><numFmt numFmtId="164" formatCode="#,##0.00"/><numFmt numFmtId="165" formatCode="yyyy-mm-dd"/><numFmt numFmtId="166" formatCode="yyyy-mm-dd h:mm AM/PM"/><numFmt numFmtId="167" formatCode="h:mm AM/PM"/></numFmts>'
        + `<fonts count="${fonts.length}">${fonts.join('')}</fonts>`
        + `<fills count="${fills.length}">${fills.join('')}</fills>`
        + `<borders count="${borders.length}">${borders.join('')}</borders>`
        + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
        + `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>`
        + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
        + '</styleSheet>';
}
/** A sheet's name: at most 31 characters and none of the seven Excel refuses. */
export function sheetName(title) {
    const clean = title.replace(/[[\]:*?/\\]/g, ' ').replace(/^'+|'+$/g, '').trim().slice(0, 31).trim();
    return clean || 'Sheet1';
}
/** How wide a column is drawn: its longest text, within reason. Characters, as Excel counts them. */
function widths(table) {
    return table.columns.map((column, i) => {
        let longest = column.label.length + (column.currency ? column.currency.length + 3 : 0);
        // A sample is enough to size a column; ten thousand rows are not measured one by one.
        for (const row of table.rows.slice(0, 500))
            longest = Math.max(longest, row[i].text.length);
        const floor = column.kind === 'datetime' ? 17 : column.kind === 'date' ? 12 : 8;
        return Math.min(60, Math.max(floor, Math.ceil(longest * 1.1) + 2));
    });
}
/** The header block's rows above the table: title, the lines about the rows, and one blank. */
function blockRows(meta, ctx) {
    return [
        { text: meta.title, style: S.title },
        ...metaLines(meta, ctx).map(([label, value]) => ({ text: `${label}: ${value}`, style: S.meta })),
    ];
}
export function sheetXml(table, meta, ctx, paper) {
    const rtl = ctx.i18n.dir === 'rtl';
    const block = blockRows(meta, ctx);
    const headRow = block.length + 2; // the block, one blank row, then the header
    const lastCol = columnName(Math.max(0, table.columns.length - 1));
    const lastRow = headRow + table.rows.length;
    const out = [];
    block.forEach((line, i) => {
        out.push(`<row r="${i + 1}"><c r="A${i + 1}" t="inlineStr" s="${line.style}"><is><t xml:space="preserve">${xmlText(line.text)}</t></is></c></row>`);
    });
    out.push(`<row r="${headRow}" ht="30" customHeight="1">${table.columns.map((c, i) => {
        const label = c.kind === 'money' && c.currency ? `${c.label} (${c.currency})` : c.label;
        return `<c r="${columnName(i)}${headRow}" t="inlineStr" s="${S.head}"><is><t xml:space="preserve">${xmlText(label)}</t></is></c>`;
    }).join('')}</row>`);
    table.rows.forEach((row, r) => {
        const n = headRow + 1 + r;
        const cells = row.map((cell, i) => {
            const column = table.columns[i];
            const ref = `${columnName(i)}${n}`;
            const number = cellNumber(column.kind, cell.raw);
            if (number !== null)
                return `<c r="${ref}" s="${KIND_STYLE[column.kind]}"><v>${number}</v></c>`;
            // No value is an empty cell, not a dash: a dash in a number column breaks the sum below it.
            if (cell.raw === null || cell.raw === undefined || cell.raw === '')
                return '';
            const style = cell.tone ? TONE_STYLE[cell.tone] : S.plain;
            return `<c r="${ref}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${xmlText(cell.text)}</t></is></c>`;
        }).join('');
        out.push(`<row r="${n}">${cells}</row>`);
    });
    // The totals: a row of their own under the table and outside its filter range, so filtering the
    // sheet never hides the line that says what the file adds up to.
    if (table.totals) {
        const n = lastRow + 1;
        out.push(`<row r="${n}">${table.totals.map((cell, i) => {
            const column = table.columns[i];
            const ref = `${columnName(i)}${n}`;
            const number = typeof cell.raw === 'number' ? cellNumber(column.kind, cell.raw) : null;
            if (number !== null)
                return `<c r="${ref}" s="${KIND_STYLE[column.kind]}"><v>${number}</v></c>`;
            return cell.text ? `<c r="${ref}" t="inlineStr" s="${S.total}"><is><t xml:space="preserve">${xmlText(cell.text)}</t></is></c>` : '';
        }).join('')}</row>`);
    }
    const cols = widths(table).map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('');
    // The declared paper (paper.ts), or A4 turned past six columns as before. A custom sheet names no
    // paper code and leaves the printer's own; its orientation still holds.
    const sheet = paperOf(paper, table.columns.length);
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
        + '<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>'
        + `<dimension ref="A1:${lastCol}${lastRow + (table.totals ? 1 : 0)}"/>`
        + `<sheetViews><sheetView workbookViewId="0"${rtl ? ' rightToLeft="1"' : ''}><pane ySplit="${headRow}" topLeftCell="A${headRow + 1}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="A${headRow + 1}" sqref="A${headRow + 1}"/></sheetView></sheetViews>`
        + '<sheetFormatPr defaultRowHeight="15"/>'
        + (cols ? `<cols>${cols}</cols>` : '')
        + `<sheetData>${out.join('')}</sheetData>`
        + (table.columns.length ? `<autoFilter ref="A${headRow}:${lastCol}${lastRow}"/>` : '')
        + '<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.6" header="0.3" footer="0.3"/>'
        + `<pageSetup${sheet.xlsx !== null ? ` paperSize="${sheet.xlsx}"` : ''} orientation="${sheet.landscape ? 'landscape' : 'portrait'}" fitToWidth="1" fitToHeight="0"/>`
        + `<headerFooter><oddFooter>${xmlText('&C&P / &N')}</oddFooter></headerFooter>`
        + '</worksheet>';
}
/** The workbook's parts, as text, by their path inside the zip. */
export function workbookParts(table, meta, ctx, paper) {
    const name = sheetName(meta.title);
    const quoted = `'${name.replace(/'/g, "''")}'`;
    const headRow = blockRows(meta, ctx).length + 2;
    const lastCol = columnName(Math.max(0, table.columns.length - 1));
    const lastRow = headRow + table.rows.length;
    const xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
    return {
        '[Content_Types].xml': `${xml}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
            + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
            + '<Default Extension="xml" ContentType="application/xml"/>'
            + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
            + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
            + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
            + '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>'
            + '</Types>',
        '_rels/.rels': `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
            + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>'
            + '</Relationships>',
        'docProps/core.xml': `${xml}<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">`
            + `<dc:title>${xmlText(meta.title)}</dc:title>`
            + (meta.by ? `<dc:creator>${xmlText(meta.by)}</dc:creator>` : '')
            + `<dcterms:created xsi:type="dcterms:W3CDTF">${xmlText(new Date(meta.at).toISOString().replace(/\.\d{3}Z$/, 'Z'))}</dcterms:created>`
            + '</cp:coreProperties>',
        'xl/workbook.xml': `${xml}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
            + `<sheets><sheet name="${xmlText(name)}" sheetId="1" r:id="rId1"/></sheets>`
            + '<definedNames>'
            + (table.columns.length ? `<definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">${xmlText(`${quoted}!$A$${headRow}:$${lastCol}$${lastRow}`)}</definedName>` : '')
            + `<definedName name="_xlnm.Print_Titles" localSheetId="0">${xmlText(`${quoted}!$${headRow}:$${headRow}`)}</definedName>`
            + '</definedNames>'
            + '</workbook>',
        'xl/_rels/workbook.xml.rels': `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
            + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
            + '</Relationships>',
        'xl/styles.xml': styles(),
        'xl/worksheets/sheet1.xml': sheetXml(table, meta, ctx, paper),
    };
}
/** The .xlsx file's bytes. */
export async function workbook(table, meta, ctx, options = {}) {
    const encoder = new TextEncoder();
    const parts = Object.entries(workbookParts(table, meta, ctx, options.paper)).map(([name, text]) => ({ name, data: encoder.encode(text) }));
    const at = new Date(meta.at);
    return zip(parts, Number.isNaN(at.getTime()) ? new Date() : at, options.compress === undefined ? {} : { compress: options.compress });
}
/** The rows a template's drop-downs and prompts cover under the header. */
const TEMPLATE_ROWS = 5000;
/** Excel's limit on a list written into its validation, quotes and separators included. */
const LIST_MAX = 255;
/** A list Excel can hold inline: none of its words carries a comma or a quote, and together they fit. */
export function inlineList(words) {
    if (!words.length || words.some((w) => !w || /[",]/.test(w)))
        return null;
    const list = words.join(',');
    return list.length + 2 <= LIST_MAX ? list : null;
}
/**
 * An import template (#28): its first sheet's first row is the template's column names, exactly as
 * the route reads them — nothing above it, because the reader takes row 1 as the header, and nothing
 * under it, because every row there is the person's (an example row would be imported). Frozen and
 * filterable, right to left for Arabic, each column as wide as its name.
 *
 * With a guide (NEW-09, human-resources-rnqqrnh7) a column of a known set offers it as a drop-down,
 * a required or explained column says so in the cell's prompt, and a second sheet — the
 * instructions, in the reader's language — lists every column: required or not, what it wants, the
 * values it takes and an example. The reader picks the sheet whose headers match, so the
 * instructions are never read as data.
 */
export async function templateWorkbook(given, title, rtl, at = new Date(), guide) {
    const columns = given.map((c) => (typeof c === 'string' ? { header: c } : c));
    const headers = columns.map((c) => c.header);
    const name = sheetName(title);
    const lastCol = columnName(Math.max(0, headers.length - 1));
    const xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
    const view = (frozen) => `<sheetViews><sheetView workbookViewId="0"${rtl ? ' rightToLeft="1"' : ''}>${frozen ? '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="A2" sqref="A2"/>' : ''}</sheetView></sheetViews>`;
    const cols = headers.map((h, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.min(60, Math.max(14, Math.ceil(h.length * 1.3) + 4))}" customWidth="1"/>`).join('');
    const text = (ref, v, style) => `<c r="${ref}" t="inlineStr" s="${style}"><is><t xml:space="preserve">${xmlText(v)}</t></is></c>`;
    // A drop-down for a known set, a prompt for what is required or explained — each over the rows to fill.
    const checks = guide ? columns.map((c, i) => {
        const list = c.choices ? inlineList(c.choices) : null;
        const prompt = [c.required ? guide.requiredPrompt : '', c.help ?? ''].filter(Boolean).join(' · ');
        if (!list && !prompt)
            return '';
        const range = `${columnName(i)}2:${columnName(i)}${TEMPLATE_ROWS + 1}`;
        const ask = prompt ? ` showInputMessage="1" promptTitle="${xmlText(c.header.slice(0, 32))}" prompt="${xmlText(prompt.slice(0, 255))}"` : '';
        return list
            ? `<dataValidation type="list" allowBlank="1" showErrorMessage="1"${ask} sqref="${range}"><formula1>${xmlText(`"${list}"`)}</formula1></dataValidation>`
            : `<dataValidation allowBlank="1"${ask} sqref="${range}"/>`;
    }).filter(Boolean) : [];
    const sheet = `${xml}<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
        + `<dimension ref="A1:${lastCol}1"/>`
        + view(true)
        + '<sheetFormatPr defaultRowHeight="15"/>'
        + (cols ? `<cols>${cols}</cols>` : '')
        + `<sheetData><row r="1" ht="30" customHeight="1">${headers.map((h, i) => text(`${columnName(i)}1`, h, S.head)).join('')}</row></sheetData>`
        + `<autoFilter ref="A1:${lastCol}1"/>`
        + (checks.length ? `<dataValidations count="${checks.length}">${checks.join('')}</dataValidations>` : '')
        + '</worksheet>';
    // The instructions: a sentence, then one row per column.
    const guideSheet = guide ? (() => {
        const heads = [guide.heads.column, guide.heads.required, guide.heads.what, guide.heads.values, guide.heads.example];
        const rows = [
            `<row r="1">${text('A1', guide.lede, S.meta)}</row>`,
            `<row r="3" ht="24" customHeight="1">${heads.map((h, i) => text(`${columnName(i)}3`, h, S.head)).join('')}</row>`,
            ...columns.map((c, n) => {
                const r = n + 4;
                const cells = [c.header, c.required ? guide.yes : guide.no, c.help ?? '', (c.choices ?? []).join(rtl ? '، ' : ', '), c.example ?? ''];
                return `<row r="${r}">${cells.map((v, i) => (v ? text(`${columnName(i)}${r}`, v, S.plain) : '')).join('')}</row>`;
            }),
        ];
        return `${xml}<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
            + `<dimension ref="A1:E${columns.length + 3}"/>`
            + view(false)
            + '<sheetFormatPr defaultRowHeight="15"/>'
            + '<cols><col min="1" max="1" width="28" customWidth="1"/><col min="2" max="2" width="10" customWidth="1"/><col min="3" max="3" width="60" customWidth="1"/><col min="4" max="4" width="40" customWidth="1"/><col min="5" max="5" width="24" customWidth="1"/></cols>'
            + `<sheetData>${rows.join('')}</sheetData>`
            + '</worksheet>';
    })() : null;
    const guideName = guide ? sheetName(guide.sheet === name ? `${guide.sheet} 2` : guide.sheet) : null;
    const parts = {
        '[Content_Types].xml': `${xml}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
            + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
            + '<Default Extension="xml" ContentType="application/xml"/>'
            + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
            + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
            + (guideSheet ? '<Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' : '')
            + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
            + '</Types>',
        '_rels/.rels': `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
            + '</Relationships>',
        'xl/workbook.xml': `${xml}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
            + `<sheets><sheet name="${xmlText(name)}" sheetId="1" r:id="rId1"/>${guideName ? `<sheet name="${xmlText(guideName)}" sheetId="2" r:id="rId3"/>` : ''}</sheets></workbook>`,
        'xl/_rels/workbook.xml.rels': `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
            + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
            + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
            + (guideSheet ? '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>' : '')
            + '</Relationships>',
        'xl/styles.xml': styles(),
        'xl/worksheets/sheet1.xml': sheet,
        ...(guideSheet ? { 'xl/worksheets/sheet2.xml': guideSheet } : {}),
    };
    const encoder = new TextEncoder();
    return zip(Object.entries(parts).map(([n, t]) => ({ name: n, data: encoder.encode(t) })), at);
}
//# sourceMappingURL=export-xlsx.js.map